/**
 * Tank ("mobile") types - GunBound-style. Each tank type defines its own
 * weapon preset (the authoritative weapon set + ammo, replacing the old mode
 * whitelist), gameplay stats, and a sprite id resolved on the client (see
 * src/render/tankSprites.ts). This module is PIXI-FREE so the headless/server
 * runtime can read presets + stats without pulling in rendering code.
 */

export type TankTypeId = string;

export interface TankStats {
  /** Absolute max-health override; undefined => inherit settings.worm.initialHealth. */
  maxHealth?: number;
  /** Per-turn walk budget (px) override; null = unlimited, undefined => inherit mode. */
  movementBudgetPx?: number | null;
}

export interface TankTypeDef {
  id: TankTypeId;
  name: string;
  description: string;
  /** Weapon -> ammo (-1 = unlimited, 0 = unavailable). Authoritative set for
   *  this tank when the mode enables tank selection. */
  ammoPreset: Record<string, number>;
  /** Sprite id resolved on the client (render/tankSprites.ts). */
  spriteId: string;
  stats: TankStats;
}

export const DEFAULT_TANK_TYPE: TankTypeId = 'worm';

/** GunBound-mobile preset: the mobile's own shots + bazooka + grenade, all at
 *  infinite ammo. */
function mobilePreset(...weaponIds: string[]): Record<string, number> {
  const preset: Record<string, number> = { bazooka: -1, grenade: -1 };
  for (const id of weaponIds) preset[id] = -1;
  return preset;
}

/** The classic worm's GunBound kit (the previous GUNBOUND_MODE whitelist, with
 *  its DEFAULT_AMMO counts inlined). Keeping the worm tank identical preserves
 *  the existing GunBound experience exactly. Inlined (not imported from
 *  DEFAULT_AMMO) to avoid a tankTypes <-> team circular import. */
const WORM_GUNBOUND_PRESET: Record<string, number> = {
  bazooka: -1,
  grenade: 10,
  grub: 4,
  grubSS: 1,
  aduka: 2,
  nak: 2,
  adukaSS: 1,
  lightningT1: 4,
  lightningT2: 2,
  knight: 2,
  trico: 2,
  kalsiddon: 2,
  frogLeft: 2,
  frogRight: 2,
  raon: 2,
  jdT2: 2,
  jdSS: 1,
  mortar: 3,
  homing: 2,
  teleport: 2,
};

export const TANK_TYPES: Record<TankTypeId, TankTypeDef> = {
  worm: {
    id: 'worm',
    name: 'Worm',
    description: 'The classic all-rounder. Full GunBound arsenal.',
    ammoPreset: WORM_GUNBOUND_PRESET,
    spriteId: 'worm',
    // Inherit HP + movement from the match settings (no override).
    stats: {},
  },
  wizard: {
    id: 'wizard',
    name: 'Wizard',
    description: 'A heavy arcane cauldron. Tanky but slow; a small magic kit.',
    ammoPreset: {
      bazooka: -1,
      lightningT1: 4,
      kalsiddon: 2,
    },
    spriteId: 'wizard',
    stats: {
      maxHealth: 250,
      // No walk limit (inherits the mode's unlimited budget, like the worm).
    },
  },

  // GunBound mobiles (use the worm sprite for now). Each carries only its own
  // shots (infinite) + bazooka + grenade. HP scaled from the official GunBound
  // stats (effective life + shield, /5) so the relative calibration holds:
  // Nak 1100, Lightning 750+300, Grub/Aduka 1000, J.Frog ~700.
  grub: {
    id: 'grub',
    name: 'Grub',
    description: 'Bouncy bug-ball lobber. Average HP.',
    // Super Shot (grubSS) is single-use; the regular shot is unlimited.
    ammoPreset: { ...mobilePreset('grub'), grubSS: 1 },
    spriteId: 'worm',
    stats: { maxHealth: 200 },
  },
  aduka: {
    id: 'aduka',
    name: 'Aduka',
    description: 'Triple Thor-strike tracers + a phase super. Average HP.',
    // Super Shot (adukaSS) is single-use; the regular shot is unlimited.
    ammoPreset: { ...mobilePreset('aduka'), adukaSS: 1 },
    spriteId: 'worm',
    stats: { maxHealth: 200 },
  },
  nak: {
    id: 'nak',
    name: 'Nak',
    description: 'Subterranean burrowing shell. High HP, hard-hitting.',
    ammoPreset: mobilePreset('nak'),
    spriteId: 'worm',
    stats: { maxHealth: 220 },
  },
  lightning: {
    id: 'lightning',
    name: 'Lightning',
    description: 'Calls down vertical / V bolts. Shielded - sturdy.',
    ammoPreset: mobilePreset('lightningT1', 'lightningT2'),
    spriteId: 'worm',
    stats: { maxHealth: 210 },
  },
  frog: {
    id: 'frog',
    name: 'J.Frog',
    description: 'Surface-crawling glob frogs (left/right). Fragile but mobile.',
    ammoPreset: mobilePreset('frogLeft', 'frogRight'),
    spriteId: 'worm',
    stats: { maxHealth: 140 },
  },
  jd: {
    id: 'jd',
    name: 'J.D.',
    description: 'Electric box-bot. Magnet shot pulls enemies in; anti-magnet SS shoves them away.',
    // Super Shot (jdSS) is single-use; the magnet shot (jdT2) is unlimited.
    ammoPreset: { ...mobilePreset('jdT2'), jdSS: 1 },
    spriteId: 'jd',
    stats: { maxHealth: 200 },
  },
};

export function getTankType(id?: string | null): TankTypeDef {
  return (id && TANK_TYPES[id]) || TANK_TYPES[DEFAULT_TANK_TYPE];
}

/** Tank ids offered in the lobby, in display order. */
export const TANK_TYPE_IDS: TankTypeId[] = [
  'worm',
  'wizard',
  'grub',
  'aduka',
  'nak',
  'lightning',
  'frog',
  'jd',
];

/** Tanks eligible for "Random tank": the GunBound mobiles (excludes the
 *  generalist worm and the heavy wizard). */
export const RANDOM_TANK_TYPE_IDS: TankTypeId[] = TANK_TYPE_IDS.filter(
  (id) => id !== 'worm' && id !== 'wizard',
);

/**
 * Assign each team a random GunBound mobile (from RANDOM_TANK_TYPE_IDS).
 * Returns NEW configs (does not mutate the input). `rand` returns 0..1 — pass
 * a seeded RNG so the assignment is reproducible from the match seed.
 */
export function assignRandomTanks<T extends { tankType?: string }>(
  teams: T[],
  rand: () => number,
): T[] {
  return teams.map((team) => ({
    ...team,
    tankType: RANDOM_TANK_TYPE_IDS[Math.floor(rand() * RANDOM_TANK_TYPE_IDS.length)],
  }));
}
